import React from 'react';
import { Card, Button, Tag, Space, Typography, Tooltip, Avatar } from 'antd';
import { DownloadOutlined, DeleteOutlined, ReloadOutlined, CheckCircleOutlined, SyncOutlined, ExclamationCircleOutlined, SettingOutlined, PauseCircleOutlined, PlayCircleOutlined, LockOutlined } from '@ant-design/icons';
import type { Plugin, PluginMetadata } from '../plugin-types';

const { Text } = Typography;

export interface PluginCardProps {
  plugin: Plugin | PluginMetadata;
  isInstalled?: boolean;
  isLoading?: boolean;
  error?: Error | null;
  isEnabled?: boolean;
  isAdmin?: boolean;
  onInstall?: (metadata: PluginMetadata) => void;
  onUninstall?: (pluginId: string) => void;
  onReload?: (pluginId: string) => void;
  onToggle?: (pluginId: string, enabled: boolean) => void;
  onConfig?: (plugin: Plugin) => void;
}

const PluginCard: React.FC<PluginCardProps> = ({
  plugin,
  isInstalled = false,
  isLoading = false,
  error = null,
  isEnabled = true,
  isAdmin = true,
  onInstall,
  onUninstall,
  onReload,
  onToggle,
  onConfig,
}) => {
  const pluginMetadata = plugin as PluginMetadata;
  const pluginData = plugin as Plugin;

  const getStatusTag = () => {
    if (error) {
      return <Tag icon={<ExclamationCircleOutlined />} color="error">加载失败</Tag>;
    }
    if (isLoading) {
      return <Tag icon={<SyncOutlined spin />} color="processing">加载中</Tag>;
    }
    if (isInstalled && isEnabled) {
      return <Tag icon={<CheckCircleOutlined />} color="success">已启用</Tag>;
    }
    if (isInstalled) {
      return <Tag icon={<CheckCircleOutlined />} color="warning">已安装</Tag>;
    }
    return null;
  };

  const getAvatarColor = () => {
    const colors = ['#1890ff', '#52c41a', '#faad14', '#13c2c2', '#722ed1', '#eb2f96'];
    const index = plugin.id.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <Card
      hoverable
      style={{ height: '100%' }}
      actions={[
        isAdmin && isInstalled ? (
          <Tooltip key="uninstall" title="卸载">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onUninstall?.(plugin.id);
              }}
              loading={isLoading}
            >
              卸载
            </Button>
          </Tooltip>
        ) : isAdmin && !isInstalled ? (
          <Tooltip key="install" title="安装">
            <Button
              type="text"
              icon={<DownloadOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onInstall?.(pluginMetadata);
              }}
              loading={isLoading}
            >
              安装
            </Button>
          </Tooltip>
        ) : !isInstalled ? (
          <Tooltip key="install-disabled" title="仅管理员可安装">
            <Button type="text" icon={<LockOutlined />} disabled>
              安装
            </Button>
          </Tooltip>
        ) : null,
        isAdmin && isInstalled && (
          <Tooltip key="toggle" title={isEnabled ? '禁用' : '启用'}>
            <Button
              type="text"
              icon={isEnabled ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onToggle?.(plugin.id, !isEnabled);
              }}
            >
              {isEnabled ? '禁用' : '启用'}
            </Button>
          </Tooltip>
        ),
        isInstalled && (
          <Tooltip key="reload" title="重新加载">
            <Button
              type="text"
              icon={<ReloadOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onReload?.(plugin.id);
              }}
            >
              重新加载
            </Button>
          </Tooltip>
        ),
      ].filter(Boolean)}
    >
      <Card.Meta
        avatar={
          <Avatar
            style={{ backgroundColor: getAvatarColor(), verticalAlign: 'middle' }}
            size="large"
          >
            {plugin.name.charAt(0)}
          </Avatar>
        }
        title={
          <Space>
            <span>{plugin.name}</span>
            {getStatusTag()}
          </Space>
        }
        description={
          <div>
            <Text type="secondary" ellipsis={{ tooltip: plugin.description }}>
              {plugin.description}
            </Text>
            <div style={{ marginTop: 8 }}>
              <Space size={4}>
                <Tag color="blue">{plugin.version}</Tag>
                {plugin.author && <Tag>{plugin.author}</Tag>}
              </Space>
            </div>
            {isInstalled && pluginData.installedAt && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  安装时间: {new Date(pluginData.installedAt).toLocaleDateString()}
                </Text>
              </div>
            )}
            {error && (
              <div style={{ marginTop: 4 }}>
                <Text type="danger" style={{ fontSize: 12 }}>
                  错误: {error.message}
                </Text>
              </div>
            )}
          </div>
        }
      />
    </Card>
  );
};

export default PluginCard;
